import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Award, Plus, Pencil, Trash2, Zap, Save, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_LEVEL = { level: "", title: "", min_xp: "" };
const EMPTY_BADGE = { badge_id: "", name: "", desc: "", icon: "star" };

export default function AdminGamificationPage() {
  const [levels, setLevels] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  const [levelEditor, setLevelEditor] = useState(null); // { original_level, form }
  const [badgeEditor, setBadgeEditor] = useState(null); // { original_badge_id, form }
  const [saving, setSaving] = useState(false);
  const [deleteLevel, setDeleteLevel] = useState(null);
  const [deleteBadge, setDeleteBadge] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lvRes, bgRes] = await Promise.all([
        axios.get(`${API}/admin/gamification/levels`),
        axios.get(`${API}/admin/gamification/badges`),
      ]);
      setLevels(lvRes.data);
      setBadges(bgRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("System admin only");
      } else {
        toast.error("Failed to load gamification config");
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── LEVELS ───

  const openLevelEditor = (lv) => {
    if (lv) {
      setLevelEditor({ original_level: lv.level, form: { level: lv.level, title: lv.title, min_xp: lv.min_xp } });
    } else {
      setLevelEditor({ original_level: null, form: { ...EMPTY_LEVEL } });
    }
  };

  const saveLevel = async () => {
    const { original_level, form } = levelEditor;
    const payload = {
      level: parseInt(form.level, 10),
      title: (form.title || "").trim(),
      min_xp: parseInt(form.min_xp, 10),
    };
    if (Number.isNaN(payload.level) || Number.isNaN(payload.min_xp) || !payload.title) {
      toast.error("Level number, title and min XP are all required");
      return;
    }
    setSaving(true);
    try {
      if (original_level == null) {
        await axios.post(`${API}/admin/gamification/levels`, payload);
        toast.success(`Level ${payload.level} added`);
      } else {
        await axios.put(`${API}/admin/gamification/levels/${original_level}`, payload);
        toast.success(`Level ${payload.level} updated`);
      }
      setLevelEditor(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save level");
    } finally {
      setSaving(false);
    }
  };

  const removeLevel = async () => {
    if (!deleteLevel) return;
    try {
      await axios.delete(`${API}/admin/gamification/levels/${deleteLevel.level}`);
      toast.success(`Level ${deleteLevel.level} removed`);
      setDeleteLevel(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete level");
    }
  };

  // ─── BADGES ───

  const openBadgeEditor = (badge) => {
    if (badge) {
      setBadgeEditor({ original_badge_id: badge.badge_id, form: { ...badge } });
    } else {
      setBadgeEditor({ original_badge_id: null, form: { ...EMPTY_BADGE } });
    }
  };

  const saveBadge = async () => {
    const { original_badge_id, form } = badgeEditor;
    const payload = {
      badge_id: (form.badge_id || "").trim(),
      name: (form.name || "").trim(),
      desc: (form.desc || "").trim(),
      icon: (form.icon || "star").trim(),
    };
    if (!payload.badge_id || !payload.name) {
      toast.error("Badge ID and name are required");
      return;
    }
    setSaving(true);
    try {
      if (original_badge_id == null) {
        await axios.post(`${API}/admin/gamification/badges`, payload);
        toast.success(`Badge "${payload.name}" added`);
      } else {
        await axios.put(`${API}/admin/gamification/badges/${original_badge_id}`, payload);
        toast.success(`Badge "${payload.name}" updated`);
      }
      setBadgeEditor(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save badge");
    } finally {
      setSaving(false);
    }
  };

  const removeBadge = async () => {
    if (!deleteBadge) return;
    try {
      await axios.delete(`${API}/admin/gamification/badges/${deleteBadge.badge_id}`);
      toast.success(`Badge "${deleteBadge.name}" removed`);
      setDeleteBadge(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete badge");
    }
  };

  const runReset = async () => {
    setResetting(true);
    setResetResult(null);
    try {
      const res = await axios.post(`${API}/admin/reset-and-seed-demo`, { confirm: true });
      setResetResult(res.data);
      toast.success(`Wiped ${res.data.deleted_users} user(s), seeded ${res.data.seeded_users}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const sortedLevels = [...levels].sort((a, b) => a.min_xp - b.min_xp);
  const sortedBadges = [...badges].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div data-testid="admin-gamification-page" className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Award className="text-[hsl(var(--primary))]" size={36} />
          Gamification
        </h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">
          System-wide levels and badges. Changes take effect immediately.
        </p>
      </div>

      <Tabs defaultValue="levels" className="space-y-6">
        <TabsList className="bg-[hsl(var(--secondary))]">
          <TabsTrigger value="levels" data-testid="tab-levels">
            <Zap size={16} className="mr-2" /> Levels ({levels.length})
          </TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges">
            <Award size={16} className="mr-2" /> Badges ({badges.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="levels" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => openLevelEditor(null)}
              className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="add-level-btn"
            >
              <Plus size={16} className="mr-2" /> Add Level
            </Button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-[hsl(var(--muted-foreground))]">Loading...</div>
          ) : (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-lg">XP Levels</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="levels-list">
                {sortedLevels.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">
                    No levels configured yet
                  </p>
                ) : (
                  sortedLevels.map((lv) => (
                    <div
                      key={lv.level}
                      className="flex items-center justify-between p-4 rounded-lg bg-[hsl(var(--secondary))]"
                      data-testid={`level-row-${lv.level}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-[hsl(var(--primary))]/15 flex items-center justify-center">
                          <span className="text-[hsl(var(--primary))] font-bold text-lg">{lv.level}</span>
                        </div>
                        <div>
                          <p className="font-semibold">{lv.title}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            Unlocks at {lv.min_xp.toLocaleString()} XP
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openLevelEditor(lv)}
                          data-testid={`edit-level-${lv.level}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteLevel(lv)}
                          className="text-red-500 hover:text-red-400"
                          data-testid={`delete-level-${lv.level}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="badges" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => openBadgeEditor(null)}
              className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
              data-testid="add-badge-btn"
            >
              <Plus size={16} className="mr-2" /> Add Badge
            </Button>
          </div>

          {loading ? (
            <div className="py-12 text-center text-[hsl(var(--muted-foreground))]">Loading...</div>
          ) : (
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-lg">Achievement Badges</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="badges-list">
                {sortedBadges.length === 0 ? (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">
                    No badges configured yet
                  </p>
                ) : (
                  sortedBadges.map((badge) => (
                    <div
                      key={badge.badge_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-[hsl(var(--secondary))]"
                      data-testid={`badge-row-${badge.badge_id}`}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-full bg-[#C36A2F]/15 flex items-center justify-center shrink-0">
                          <Award className="text-[#C36A2F]" size={20} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{badge.name}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                            {badge.desc || badge.badge_id}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openBadgeEditor(badge)}
                          data-testid={`edit-badge-${badge.badge_id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteBadge(badge)}
                          className="text-red-500 hover:text-red-400"
                          data-testid={`delete-badge-${badge.badge_id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Level Editor Dialog */}
      <Dialog open={!!levelEditor} onOpenChange={(open) => !open && setLevelEditor(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="level-editor-dialog">
          <DialogHeader>
            <DialogTitle>{levelEditor?.original_level == null ? "Add Level" : `Edit Level ${levelEditor?.original_level}`}</DialogTitle>
          </DialogHeader>
          {levelEditor && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Level Number *</Label>
                  <Input
                    type="number"
                    value={levelEditor.form.level}
                    onChange={(e) => setLevelEditor({ ...levelEditor, form: { ...levelEditor.form, level: e.target.value } })}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="level-input-level"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Min XP *</Label>
                  <Input
                    type="number"
                    value={levelEditor.form.min_xp}
                    onChange={(e) => setLevelEditor({ ...levelEditor, form: { ...levelEditor.form, min_xp: e.target.value } })}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="level-input-min-xp"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={levelEditor.form.title}
                  onChange={(e) => setLevelEditor({ ...levelEditor, form: { ...levelEditor.form, title: e.target.value } })}
                  placeholder="e.g. Mixologist"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="level-input-title"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setLevelEditor(null)} disabled={saving}>
                  <X size={14} className="mr-1" /> Cancel
                </Button>
                <Button
                  onClick={saveLevel}
                  disabled={saving}
                  className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                  data-testid="save-level-btn"
                >
                  <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Badge Editor Dialog */}
      <Dialog open={!!badgeEditor} onOpenChange={(open) => !open && setBadgeEditor(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="badge-editor-dialog">
          <DialogHeader>
            <DialogTitle>{badgeEditor?.original_badge_id == null ? "Add Badge" : `Edit Badge`}</DialogTitle>
          </DialogHeader>
          {badgeEditor && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Badge ID * <span className="text-xs text-[hsl(var(--muted-foreground))]">(machine name, e.g. perfect_score)</span></Label>
                <Input
                  value={badgeEditor.form.badge_id}
                  onChange={(e) => setBadgeEditor({ ...badgeEditor, form: { ...badgeEditor.form, badge_id: e.target.value.toLowerCase().replace(/\s+/g, '_') } })}
                  placeholder="my_new_badge"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="badge-input-id"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input
                  value={badgeEditor.form.name}
                  onChange={(e) => setBadgeEditor({ ...badgeEditor, form: { ...badgeEditor.form, name: e.target.value } })}
                  placeholder="Perfect Score"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="badge-input-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={badgeEditor.form.desc}
                  onChange={(e) => setBadgeEditor({ ...badgeEditor, form: { ...badgeEditor.form, desc: e.target.value } })}
                  placeholder="Score 100% on any challenge"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="badge-input-desc"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon <span className="text-xs text-[hsl(var(--muted-foreground))]">(Lucide icon name, e.g. star, flame, trophy)</span></Label>
                <Input
                  value={badgeEditor.form.icon}
                  onChange={(e) => setBadgeEditor({ ...badgeEditor, form: { ...badgeEditor.form, icon: e.target.value } })}
                  placeholder="star"
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="badge-input-icon"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" onClick={() => setBadgeEditor(null)} disabled={saving}>
                  <X size={14} className="mr-1" /> Cancel
                </Button>
                <Button
                  onClick={saveBadge}
                  disabled={saving}
                  className="bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                  data-testid="save-badge-btn"
                >
                  <Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Level Confirmation */}
      <AlertDialog open={!!deleteLevel} onOpenChange={() => setDeleteLevel(null)}>
        <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Level?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove Level {deleteLevel?.level} &middot; {deleteLevel?.title}? Users currently at this level will be reassigned to the next-lower level automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeLevel} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-level">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Badge Confirmation */}
      <AlertDialog open={!!deleteBadge} onOpenChange={() => setDeleteBadge(null)}>
        <AlertDialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Badge?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{deleteBadge?.name}&rdquo;? Any user who already earned it will lose their award. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={removeBadge} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-badge">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Danger Zone — Reset & Seed Demo Data */}
      <Card className="bg-red-950/20 border-red-500/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={20} /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Wipes every user except you, deletes the &ldquo;Gaucho Admin&rdquo; venue, then creates two demo staff (Sarah Bartender + Tom Barback, password <code className="text-xs bg-[hsl(var(--secondary))] px-1.5 py-0.5 rounded">DemoPass123!</code>) and seeds fake XP + test results so leaderboards look populated. This action is destructive and cannot be undone.
          </p>
          <Button
            onClick={() => { setResetResult(null); setShowResetConfirm(true); }}
            variant="destructive"
            className="bg-red-600 hover:bg-red-700"
            data-testid="reset-demo-btn"
          >
            <AlertTriangle size={16} className="mr-2" /> Reset &amp; Seed Demo Data
          </Button>
          {resetResult && (
            <div className="mt-3 p-3 rounded-lg bg-green-950/30 border border-green-500/40 text-sm space-y-1" data-testid="reset-result">
              <p className="text-green-400 font-semibold">Reset complete</p>
              <p>Deleted users: <span className="font-mono">{resetResult.deleted_users}</span></p>
              <p>Demo users created: <span className="font-mono">{(resetResult.demo_users_created || []).map(d => d.name).join(", ")}</span></p>
              <p>Demo password: <span className="font-mono">DemoPass123!</span></p>
              {resetResult.admin_venue_deleted && <p>&ldquo;Gaucho Admin&rdquo; venue deleted</p>}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent className="bg-[hsl(var(--card))] border-red-500/40">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle size={20} /> Reset all user data?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>permanently delete every user account except your own</strong> along with their test history, XP, badges, sessions and staff records. The &ldquo;Gaucho Admin&rdquo; venue will also be removed. Two demo staff (Sarah Bartender + Tom Barback) will be created and everyone will get realistic fake XP + test results.
              <br /><br />
              Only run this once during the test period. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { await runReset(); setShowResetConfirm(false); }}
              className="bg-red-600 hover:bg-red-700"
              disabled={resetting}
              data-testid="confirm-reset-demo"
            >
              {resetting ? "Resetting..." : "Yes, wipe & reseed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
